import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmModal } from '@/src/modules/components/settings/ConfirmModal';

export interface InsufficientWalletModalProps {
  open: boolean;
  onClose: () => void;
  isWalletUser?: boolean;
  neededRupees?: number;
  availableRupees?: number;
  neededCredits?: number;
  availableCredits?: number;
  message?: string;
  onAddMoney?: () => void;
  onBuyCredits?: () => void;
}

/**
 * Shared modal shown when a user action (publish, extend, republish) is refused
 * due to insufficient balance (HTTP 402 / NO_PUBLISH_RIGHT — PQ-08).
 *
 * For wallet users (partners), offers a link to top up the wallet (/dashboard/settings?section=subscription).
 * For credit users (individual / retail), offers a link to buy credits / collections (/catalog).
 */
export function InsufficientWalletModal({
  open,
  onClose,
  isWalletUser = true,
  neededRupees,
  availableRupees,
  neededCredits,
  availableCredits,
  message,
  onAddMoney,
  onBuyCredits,
}: InsufficientWalletModalProps) {
  const navigate = useNavigate();

  const title = isWalletUser ? 'Not enough wallet balance' : 'Not enough Publish Credits';
  const confirmLabel = isWalletUser ? 'Buy Partner Pack' : 'Buy a Publish Credit';

  const description = React.useMemo(() => {
    if (message) return message;
    if (isWalletUser) {
      if (neededRupees !== undefined && availableRupees !== undefined) {
        return `₹${neededRupees.toLocaleString('en-IN')} needed, ₹${availableRupees.toLocaleString('en-IN')} available.`;
      }
      return 'Your wallet balance is not enough to complete this action.';
    }

    if (neededCredits !== undefined && availableCredits !== undefined) {
      return `${neededCredits} ${neededCredits === 1 ? 'credit' : 'credits'} needed, ${availableCredits} available.`;
    }
    return 'You do not have enough publish credits to complete this action.';
  }, [isWalletUser, neededRupees, availableRupees, neededCredits, availableCredits, message]);

  const handleConfirm = () => {
    if (isWalletUser) {
      if (onAddMoney) {
        onAddMoney();
      } else {
        navigate('/dashboard/partner?tab=credits');
      }
    } else {
      if (onBuyCredits) {
        onBuyCredits();
      } else {
        navigate('/catalog');
      }
    }
    onClose();
  };

  return (
    <ConfirmModal
      open={open}
      tone="primary"
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      cancelLabel="Cancel"
      onClose={onClose}
      onConfirm={handleConfirm}
    />
  );
}
